/* eslint-disable */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, BookOpen, ClipboardList } from 'lucide-react';
import Select from "react-select";
import adminServices from '../../../../services/adminServices';
import Modal from '../../../../components/modal/Index';
import SimpleSnackbar from '../../../../components/snackbar/snackbar';

// ─── helpers ─────────────────────────────────────────────────────────────────

function groupBySubscription(rows) {
    const map = {};
    for (const row of rows) {
        const key = row.planName || '—';
        if (!map[key]) map[key] = [];
        map[key].push(row);
    }
    return Object.entries(map);
}

function fmt(date) {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_BADGE = {
    ACTIVE: 'bg-green-100 text-green-700',
    EXPIRED: 'bg-gray-100 text-gray-500',
    DELETED: 'bg-red-100 text-red-500',
};

// ─── Assign Modal (replaces AssignDrawer) ─────────────────────────────────────

function AssignPlanModal({ clientId, schemaName, isOpen, onClose, onSuccess, assignedPlans = [] }) {
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [contract, setContract] = useState(null);
    const [contractLoading, setContractLoading] = useState(true);
    const [form, setForm] = useState({ templateId: '', name: '', maxAttempts: '' });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setForm({ templateId: '', name: '', maxAttempts: '' });
        setError('');
        setLoadingTemplates(true);
        setContractLoading(true);


        adminServices.getB2BTemplatePlans()
            .then(r => {
                if (r.ok) setTemplates((r.data || []).filter(plan => plan.isOnline));
            })
            .catch(() => { })
            .finally(() => setLoadingTemplates(false));

        adminServices.getClientContract(clientId, schemaName)
            .then(r => { if (r.ok) setContract(r.data || null); })
            .catch(() => { })
            .finally(() => setContractLoading(false));
    }, [isOpen, clientId]);

    const hasContract = !contractLoading && contract && contract.startDate && contract.endDate;

    const assignedPlanNames = new Set(
        assignedPlans.map(p => p.planName).filter(Boolean)
    );
    const handleTemplateChange = (e) => {
        const tplId = e.target.value;
        const tpl = templates.find(t => String(t.id) === String(tplId));
        setForm(f => ({ ...f, templateId: tplId, name: tpl ? tpl.name : '' }));
    };

    const handleSubmit = async () => {
        setError('');
        if (!hasContract) {
            setError('Contract details are missing. Please add contract details in the Contract tab before assigning a plan.');
            return;
        }
        if (!form.templateId) { setError('Please select a plan template.'); return; }
        if (!form.name.trim()) { setError('Please enter a plan name.'); return; }
        if (form.maxAttempts !== '' && (isNaN(Number(form.maxAttempts)) || Number(form.maxAttempts) < 1)) {
            setError('Max attempts must be a positive number.');
            return;
        }

        setSubmitting(true);
        try {
            const selectedTemplate = templates.find(t => String(t.id) === String(form.templateId));
            const isAssessment = !!(selectedTemplate?.assessmentDetils && selectedTemplate.assessmentDetils.length > 0);
            let contentType = "";
            const hasAssessment = Array.isArray(selectedTemplate?.assessmentDetils) && selectedTemplate.assessmentDetils.length > 0;
            const hasCourse = Array.isArray(selectedTemplate?.courseDetails) && selectedTemplate.courseDetails.length > 0;

            if (hasAssessment && hasCourse) {
                contentType = "Both";
            } else if (hasAssessment) {
                contentType = "Assessments";
            } else if (hasCourse) {
                contentType = "Course";
            }
            const r = await adminServices.assignB2BTemplateToClient({
                templateId: Number(form.templateId),
                clientId: Number(clientId),
                name: form.name,
                schemaName,
                validFrom: contract.startDate,
                validTo: contract.endDate,
                noOfLicenses: contract.learnerSeats ? Number(contract.learnerSeats) : 0,
                remarks: null,
                is_assessment: isAssessment,
                scope_type: contentType,
                ...(form.maxAttempts !== '' && { maxAttempts: Number(form.maxAttempts) }),
            });
            if (r.ok) {
                onSuccess();
                onClose();
            } else {
                setError(r.data?.message || 'Assignment failed.');
            }
        } catch {
            setError('Assignment failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const fmtDate = (d) =>
        d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

    return (
        <Modal title="Assign B2B Plan" open={isOpen} toggle={onClose}>
            <div className="space-y-4">

                {/* Contract details banner */}
                {contractLoading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
                        <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                        Loading contract details…
                    </div>
                ) : hasContract ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs space-y-1">
                        <p className="font-semibold text-blue-700 mb-1.5">Contract Details (from client record)</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-blue-700">
                            <span className="text-blue-500">Valid From</span>
                            <span className="font-medium">{fmtDate(contract.startDate)}</span>
                            <span className="text-blue-500">Valid To</span>
                            <span className="font-medium">{fmtDate(contract.endDate)}</span>
                            <span className="text-blue-500">Learner Seats</span>
                            <span className="font-medium">{contract.learnerSeats || '—'}</span>
                        </div>
                    </div>
                ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-xs text-red-700 flex items-start gap-2">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                        <span>
                            <strong>Contract details are missing.</strong> Please add contract details (Start Date, End Date,
                            Learner Seats) in the <strong>Contract tab</strong> before assigning a plan.
                        </span>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                        Plan Template <span className="text-red-500">*</span>
                    </label>

                    {loadingTemplates ? (
                        <p className="text-xs text-gray-400">Loading templates…</p>
                    ) : (
                        <Select
                            value={
                                templates
                                    .map(t => ({
                                        value: t.id,
                                        label: t.name || t.displayName,
                                    }))
                                    .find(option => option.value === form.templateId) || null
                            }
                            onChange={(selected) =>
                                handleTemplateChange({
                                    target: {
                                        value: selected?.value || "",
                                    },
                                })
                            }
                            isDisabled={!hasContract}
                            isSearchable
                            placeholder="Search and select template..."
                            options={templates
                                .filter(t => {
                                    const isAssigned = [...assignedPlanNames].some(
                                        name => name.toLowerCase() === (t.name || "").toLowerCase()
                                    );
                                    const isSelected = t.id === form.templateId;

                                    return !isAssigned && !isSelected;
                                })
                                .map(t => ({
                                    value: t.id,
                                    label: t.name || t.displayName,
                                }))}
                            className="text-sm"
                            classNamePrefix="react-select"
                        />
                    )}
                </div>

                {form.templateId && (() => {
                    const tpl = templates.find(t => String(t.id) === String(form.templateId));
                    if (!tpl) return null;
                    const courses = tpl.courseDetailsNew || [];
                    const assessments = tpl.assessmentDetils || [];
                    return (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-xs space-y-2">
                            {courses.length > 0 && (
                                <div>
                                    <p className="font-medium text-gray-700 flex items-center gap-1">
                                        <BookOpen size={12} /> Courses ({courses.length})
                                    </p>
                                    <ul className="mt-1 space-y-0.5 pl-3 text-gray-500">
                                        {courses.map(c => <li key={c.id}>• {c.title}</li>)}
                                    </ul>
                                </div>
                            )}
                            {assessments.length > 0 && (
                                <div>
                                    <p className="font-medium text-gray-700 flex items-center gap-1">
                                        <ClipboardList size={12} /> Assessments ({assessments.length})
                                    </p>
                                    <ul className="mt-1 space-y-0.5 pl-3 text-gray-500">
                                        {assessments.map(a => <li key={a.id}>• {a.title}</li>)}
                                    </ul>
                                </div>
                            )}
                            {courses.length === 0 && assessments.length === 0 && (
                                <p className="text-gray-400">No content in this template.</p>
                            )}
                        </div>
                    );
                })()}

                {/* Plan name */}
                <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                        Plan Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        disabled={!hasContract}
                        placeholder="e.g. Q1 2026 Enterprise Pack"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 hover:border-gray-400 transition-all"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                        Max Paid Attempts per Assessment
                        {/* <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span> */}
                    </label>
                    <input
                        type="number"
                        min="1"
                        value={form.maxAttempts}
                        onChange={e => setForm(f => ({ ...f, maxAttempts: e.target.value }))}
                        disabled={!hasContract}
                        placeholder="5"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 hover:border-gray-400 transition-all"
                    />
                    <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                        Free attempt always included — 1 per assessment, all tiers
                    </p>
                </div>

                {/* Error */}
                {error && (
                    <p className="text-red-500 text-xs">⚠ {error}</p>
                )}

                {/* Footer buttons */}
                <div className="flex gap-3 justify-end pt-2 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || !hasContract}
                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {submitting && (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                        {submitting ? 'Assigning…' : 'Assign Plan'}
                    </button>
                </div>

            </div>
        </Modal>
    );
}

// ─── Unassign confirm ─────────────────────────────────────────────────────────

function UnassignConfirm({ planName, onConfirm, onCancel, loading }) {
    return (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Unassign Plan</h3>
                <p className="text-sm text-gray-500 mb-6">
                    Are you sure you want to unassign{' '}
                    <span className="font-medium text-gray-700">"{planName}"</span>?
                    This will deactivate the plan for this client.
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={onCancel}
                        className="flex-1 border border-gray-300 text-sm text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className="flex-1 bg-red-600 text-sm text-white py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                        {loading ? 'Removing…' : 'Unassign'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main PlansTab ────────────────────────────────────────────────────────────

export default function PlansTab({ clientDetails }) {
    const clientId = clientDetails?.id;
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openGroups, setOpenGroups] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [unassignTarget, setUnassignTarget] = useState(null);
    const [unassignLoading, setUnassignLoading] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarTitle, setSnackbarTitle] = useState('');
    const [snackbarType, setSnackbarType] = useState('success');

    const showSnackbar = (message, type = 'success') => {
        setSnackbarTitle(message);
        setSnackbarType(type);
        setOpenSnackbar(true);
    };

    const load = () => {
        if (!clientId) return;
        setLoading(true);
        adminServices.getSubscriptionData(clientId, clientDetails?.slug ? `tenant_${clientDetails.slug}` : 'public')
            .then(r => {
                if (r.ok) {
                    setPlans(r.data || []);
                    const groups = {};
                    (r.data || []).forEach(row => { groups[row.subscriptionName || '—'] = true; });
                    setOpenGroups(groups);
                } else {
                    setError(r.data?.message || 'Failed to load plans.');
                }
            })
            .catch(() => setError('Failed to load plans.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [clientId]);

    const toggleGroup = (key) => setOpenGroups(g => ({ ...g, [key]: !g[key] }));

    const handleUnassign = async () => {
        if (!unassignTarget) return;
        setUnassignLoading(true);
        try {
            const r = await adminServices.deleteSubsrciptionplanByID(unassignTarget.id);
            if (r.ok) {
                showSnackbar('Plan unassigned successfully.', 'success');
                setUnassignTarget(null);
                load();
            } else {
                showSnackbar(r.data?.message || 'Failed to unassign.', 'error');
                setUnassignTarget(null);
            }
        } catch {
            showSnackbar('Failed to unassign.', 'error');
            setUnassignTarget(null);
        } finally {
            setUnassignLoading(false);
        }
    };

    const groups = groupBySubscription(plans);

    return (
        <div>
            {/* Header row */}
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h2 className="text-base font-semibold text-gray-900">Assigned Plans</h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                        {groups?.length} plan{groups?.length !== 1 ? 's' : ''} assigned
                    </p>
                </div>
                <button
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-1.5 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                    <Plus size={15} />
                    Assign Plan
                </button>
            </div>

            {/* Content */}
            {loading ? (
                <div className="text-center py-16 text-gray-400 text-sm">Loading plans…</div>
            ) : error ? (
                <div className="text-center py-16 text-red-500 text-sm">{error}</div>
            ) : groups.length === 0 ? (
                <div className="text-center py-16 text-gray-400 text-sm">
                    No plans assigned yet.
                    <br />
                    <button
                        onClick={() => setModalOpen(true)}
                        className="mt-2 text-blue-600 hover:underline text-sm"
                    >
                        + Assign your first plan
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    {groups.map(([subName, rows]) => (
                        <div key={subName} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Group header */}
                            <button
                                onClick={() => toggleGroup(subName)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                                <div className="w-full flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        {openGroups[subName]
                                            ? <ChevronDown size={15} className="text-gray-400" />
                                            : <ChevronRight size={15} className="text-gray-400" />}
                                        <span className="text-sm font-semibold text-gray-800">{subName}</span>
                                        <span className="text-xs text-gray-400">
                                            ({rows.length} item{rows.length !== 1 ? 's' : ''})
                                        </span>
                                        {rows[0]?.maxAttempts != null && (
                                            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                                                {rows[0].maxAttempts} max attempts
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setUnassignTarget({
                                            id: rows[0].id,
                                            name: rows[0].clientPlanName || rows[0].planName || 'this plan'
                                        })}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                        title="Unassign"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </button>

                            {/* Rows */}
                            {openGroups[subName] && (
                                <div className="divide-y divide-gray-100">
                                    {rows.map(row => (
                                        <div key={row.id} className="px-4 py-3 flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                <div className="mt-0.5 flex-shrink-0">
                                                    {row.assessmentTitle
                                                        ? <ClipboardList size={15} className="text-purple-500" />
                                                        : <BookOpen size={15} className="text-blue-500" />}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 truncate">
                                                        {row.assessmentTitle || row.courseName || row.clientPlanName || '—'}
                                                    </p>
                                                    <p className="text-xs text-gray-400 mt-0.5">
                                                        {row.assessmentTitle ? 'Assessment' : 'Course'}
                                                        {' · '}Plan: {row.planName || '—'}
                                                    </p>
                                                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                                        <span>{fmt(row.validFrom)} – {fmt(row.validTo)}</span>
                                                        {/* {row.noOfLicenseAllocated > 0 && (
                                                            <span>{row.noOfLicenseAllocated} licenses</span>
                                                        )} */}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3 flex-shrink-0">
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[row.status] || 'bg-gray-100 text-gray-500'}`}>
                                                    {row.status}
                                                </span>

                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Assign Plan Modal */}
            <AssignPlanModal
                clientId={clientId}
                schemaName={clientDetails?.slug ? `tenant_${clientDetails.slug}` : 'public'}
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={load}
                assignedPlans={plans}
            />

            {/* Unassign confirm */}
            {unassignTarget && (
                <UnassignConfirm
                    planName={unassignTarget.name}
                    onConfirm={handleUnassign}
                    onCancel={() => setUnassignTarget(null)}
                    loading={unassignLoading}
                />
            )}

            {/* Snackbar */}
            <SimpleSnackbar
                open={openSnackbar}
                snackbarTitle={snackbarTitle}
                snackbarType={snackbarType}
                close={() => setOpenSnackbar(false)}
            />
        </div>
    );
}